
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Pencil, Trash, FileText, Network } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDateTime } from "@/utils/dateUtils";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Diagram } from "@/utils/diagramService";

interface DiagramTableProps {
  diagrams: Diagram[];
  loading: boolean;
  onSort: (column: string) => void;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  onEdit: (diagram: Diagram) => void;
  onDelete: (diagram: Diagram) => void;
}

export function DiagramTable({
  diagrams,
  loading,
  onSort,
  sortColumn,
  sortDirection,
  onEdit,
  onDelete,
}: DiagramTableProps) {
  const navigate = useNavigate();

  const handleSort = (column: string) => {
    onSort(column);
  };

  const openEditor = (diagramId: string) => {
    navigate(`/diagrama/${diagramId}`);
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg overflow-hidden border border-border/40">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border">
              <TableHead className="font-semibold text-muted-foreground">Nome</TableHead>
              <TableHead className="font-semibold text-muted-foreground">Criado em</TableHead>
              <TableHead className="font-semibold text-muted-foreground">Modificado em</TableHead>
              <TableHead className="font-semibold text-muted-foreground text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} className="text-center py-12">
                <div className="flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">Carregando diagramas...</p>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border/40">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border">
            <TableHead 
              className="font-semibold text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => handleSort("name")}
            >
              Nome
            </TableHead>
            <TableHead 
              className="font-semibold text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => handleSort("created_at")}
            >
              Criado em
            </TableHead>
            <TableHead 
              className="font-semibold text-muted-foreground cursor-pointer hover:text-foreground" 
              onClick={() => handleSort("modified_at")}
            >
              Modificado em
            </TableHead>
            <TableHead className="font-semibold text-muted-foreground text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {diagrams.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-12">
                <p className="text-sm text-muted-foreground">Nenhum diagrama encontrado.</p>
              </TableCell>
            </TableRow>
          ) : (
            diagrams.map((diagram) => (
              <TableRow key={diagram.id} className="hover:bg-muted/20">
                <TableCell className="font-medium">{diagram.name}</TableCell>
                <TableCell className="text-muted-foreground">{formatDateTime(diagram.created_at)}</TableCell>
                <TableCell className="text-muted-foreground">{formatDateTime(diagram.modified_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditor(diagram.id)}
                      title="Abrir editor de diagrama"
                    >
                      <Network className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => navigate(`/diagrama-detalhes/${diagram.id}`)}
                      title="Detalhes"
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEdit(diagram)}
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => onDelete(diagram)}
                      title="Excluir"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
